export interface Translations {
  nav: {
    home: string;
    about: string;
    services: string;
    contact: string;
  };
  hero: {
    title: string;
    subtitle: string;
    description: string;
    cta: string;
    label: string;
    learnMore: string;
    proof: string;
  };
  features: {
    title: string;
    items: Array<{
      title: string;
      description: string;
    }>;
  };
  stats: {
    title: string;
    items: Array<{
      value: string;
      label: string;
    }>;
  };
  about: {
    title: string;
    description: string;
    team: {
      title: string;
      experience: string;
      projects: string;
      certified: string;
      members: Array<{
        role: string;
        skills: string;
      }>;
    };
  };
  services: {
    title: string;
    subtitle: string;
    packages: Array<{
      title: string;
      description: string;
      features: string[];
    }>;
  };
  clients: {
    title: string;
    subtitle: string;
    list: Array<{
      name: string;
      industry: string;
      project: string;
    }>;
  };
  contact: {
    title: string;
    address: string;
    phone: string;
    email: string;
    form: {
      name: string;
      email: string;
      message: string;
      send: string;
      sending: string;
      success: string;
      error: string;
    };
  };
  footer: {
    company: string;
    tagline: string;
    description: string;
    copyright: string;
    services: {
      title: string;
      list: string[];
    };
    contact: {
      title: string;
    };
    taxId: string;
  };
}

export const translations: { th: Translations; en: Translations } = {
  th: {
    nav: {
      home: 'หน้าแรก',
      about: 'เกี่ยวกับเรา',
      services: 'บริการ',
      contact: 'ติดต่อ'
    },
    hero: {
      title: 'โซลูชันเทคโนโลยีครบวงจรสำหรับองค์กรสมัยใหม่',
      subtitle: 'AI, Blockchain และระบบผสานข้อมูล เพื่อประสิทธิภาพสูงสุด',
      description: 'พัฒนาองค์กรด้วยเทคโนโลยี AI & Computer Vision, โซลูชัน Blockchain & Crypto และบริการผสานระบบที่ทันสมัย',
      cta: 'ปรึกษาโซลูชัน',
      label: 'Technology Solutions',
      learnMore: 'เรียนรู้เพิ่มเติม',
      proof: 'ใช้งานจริงใน สคบ. · สบน. · สนช. · Bundhaya Speedboat'
    },
    features: {
      title: 'สิ่งที่เรามอบให้',
      items: [
        {
          title: 'Advanced AI Technology',
          description: 'Large Language Models ระดับ Enterprise'
        },
        {
          title: 'White Label Platform',
          description: 'ปรับแต่งตามอัตลักษณ์องค์กร'
        },
        {
          title: 'Comprehensive Analytics',
          description: 'ระบบวิเคราะห์และรายงานผล'
        },
        {
          title: 'Rapid Implementation',
          description: 'การติดตั้งที่รวดเร็วและง่ายดาย'
        },
        {
          title: 'Cross-Platform Support',
          description: 'รองรับทุกอุปกรณ์และแพลตฟอร์ม'
        },
        {
          title: 'Enterprise Security',
          description: 'มาตรฐานความปลอดภัยระดับองค์กร'
        }
      ]
    },
    stats: {
      title: 'ผลลัพธ์ทางธุรกิจที่วัดผลได้',
      items: [
        { value: '5+', label: 'ปีประสบการณ์' },
        { value: '10+', label: 'โปรเจกต์สำเร็จ' },
        { value: '100%', label: 'ความพึงพอใจ' },
        { value: '24/7', label: 'ระบบสนับสนุน' }
      ]
    },
    about: {
      title: 'เกี่ยวกับเรา',
      description: 'เราเป็นผู้เชี่ยวชาญด้านการพัฒนา AI Solutions, Blockchain & Crypto และระบบผสานข้อมูลสำหรับองค์กรชั้นนำ ด้วยประสบการณ์กว่า 5 ปีในการสร้างเทคโนโลยีที่ทันสมัยและมีประสิทธิภาพ',
      team: {
        title: 'ทีมงานมืออาชีพ',
        experience: '5+ ปี ในการพัฒนา AI/ML, Blockchain & Enterprise Solutions',
        projects: '10+ โปรเจกต์ที่ประสบความสำเร็จ',
        certified: 'Certified Partners ของ Cloud Providers ชั้นนำ',
        members: [
          {
            role: 'AI/ML Engineer',
            skills: 'Python, TensorFlow, PyTorch, NLP, Computer Vision'
          },
          {
            role: 'Blockchain Developer',
            skills: 'Solidity, Web3.js, Smart Contracts, DeFi, NFT'
          },
          {
            role: 'Backend Developer',
            skills: 'Node.js, FastAPI, Docker, Kubernetes'
          },
          {
            role: 'Cloud Architect',
            skills: 'AWS, GCP, Azure, Infrastructure as Code'
          }
        ]
      }
    },
    services: {
      title: 'บริการของเรา',
      subtitle: 'โซลูชันเทคโนโลยีครบวงจรสำหรับองค์กรสมัยใหม่',
      packages: [
        {
          title: 'AI & Machine Learning Solutions',
          description: 'ระบบ AI ขั้นสูงสำหรับการสื่อสารและการมองเห็น',
          features: [
            'Conversational AI & Chatbot Development',
            'Computer Vision & Image Recognition',
            'Natural Language Processing (NLP)',
            'Custom AI Model Training & Deployment',
            'Google Cloud Vision AI Integration',
            'Automated Document Processing (OCR)',
            'Real-time Object Detection & Tracking'
          ]
        },
        {
          title: 'Blockchain & Crypto Solutions',
          description: 'โซลูชันบล็อกเชนและสกุลเงินดิจิทัลสำหรับองค์กรและนักลงทุน',
          features: [
            'Smart Contract Development (DeFi/NFT)',
            'Cryptocurrency Trading Bot Systems',
            'Blockchain Application (DApp) Development',
            'Wallet Integration & Payment Gateway',
            'Portfolio Management & Analytics Tools',
            'Market Data Analysis & Risk Management',
            'Crypto Exchange Integration'
          ]
        },
        {
          title: 'Enterprise Integration Services',
          description: 'บริการเชื่อมต่อและปรับปรุงระบบองค์กร รวมถึงการเปลี่ยนผ่านดิจิทัล',
          features: [
            'Digital Transformation & Process Automation',
            'Legacy System Modernization',
            'Cloud Migration & Infrastructure Setup',
            'API Development & Third-party Integration',
            'Database Design & Optimization',
            'DevOps & Continuous Deployment',
            'Security Implementation & Compliance',
            '24/7 System Monitoring & Support'
          ]
        }
      ]
    },
    clients: {
      title: 'ลูกค้าของเรา',
      subtitle: 'องค์กรชั้นนำที่ไว้วางใจเทคโนโลยีของเรา',
      list: [
        {
          name: 'Sansiri',
          industry: 'อสังหาริมทรัพย์',
          project: 'น้องแสนรู้ - AI Assistant'
        }
      ]
    },
    contact: {
      title: 'ติดต่อเรา',
      address: '69/75 ถนนพญาไท แขวงถนนพญาไท เขตราชเทวี กรุงเทพมหานคร 10400',
      phone: '+66 87-713-1110',
      email: 'contact@onionshack.com',
      form: {
        name: 'ชื่อ',
        email: 'อีเมล',
        message: 'ข้อความ',
        send: 'ส่งข้อความ',
        sending: 'กำลังส่ง...',
        success: 'ข้อความถูกส่งแล้ว! เราจะติดต่อกลับภายใน 24 ชั่วโมง',
        error: 'เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองใหม่อีกครั้ง'
      }
    },
    footer: {
      company: 'บริษัท ออนเนียนแชค จำกัด',
      tagline: 'เปลี่ยนธุรกิจด้วยเทคโนโลยี AI, Blockchain และการผสานระบบ',
      description: 'ผู้เชี่ยวชาญด้านการพัฒนา AI Solutions, Blockchain & Crypto และระบบผสานข้อมูลสำหรับองค์กรชั้นนำ ด้วยประสบการณ์กว่า 5 ปีในการสร้างเทคโนโลยีที่ทันสมัยและมีประสิทธิภาพ',
      copyright: '© 2025 Onionshack Company Limited สงวนลิขสิทธิ์',
      services: {
        title: 'บริการ',
        list: ['AI & Machine Learning', 'Computer Vision', 'Blockchain & Crypto', 'Enterprise Integration']
      },
      contact: {
        title: 'ติดต่อ'
      },
      taxId: 'เลขประจำตัวผู้เสียภาษี: 0105560146233'
    }
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      contact: 'Contact'
    },
    hero: {
      title: 'Comprehensive Technology Solutions for Modern Enterprises',
      subtitle: 'AI, Blockchain & System Integration for Maximum Efficiency',
      description: 'Transform your organization with AI & Computer Vision technology, Blockchain & Crypto solutions, and modern system integration services',
      cta: 'Consult Solutions',
      label: 'Technology Solutions',
      learnMore: 'Learn More',
      proof: 'Live at OCPB · PDMO · NIA · Bundhaya Speedboat'
    },
    features: {
      title: 'Excellence We Deliver',
      items: [
        {
          title: 'Advanced AI Technology',
          description: 'Enterprise-grade Large Language Models'
        },
        {
          title: 'White Label Platform',
          description: 'Customizable to match your brand identity'
        },
        {
          title: 'Comprehensive Analytics',
          description: 'Analysis and reporting systems'
        },
        {
          title: 'Rapid Implementation',
          description: 'Quick and easy installation'
        },
        {
          title: 'Cross-Platform Support',
          description: 'Compatible with all devices and platforms'
        },
        {
          title: 'Enterprise Security',
          description: 'Enterprise-level security standards'
        }
      ]
    },
    stats: {
      title: 'Measurable Business Results',
      items: [
        { value: '5+', label: 'Years Experience' },
        { value: '10+', label: 'Successful Projects' },
        { value: '100%', label: 'Client Satisfaction' },
        { value: '24/7', label: 'System Support' }
      ]
    },
    about: {
      title: 'About Us',
      description: 'We are experts in developing AI Solutions, Blockchain & Crypto, and system integration services for leading organizations. With over 5 years of experience, we create modern and efficient technology solutions.',
      team: {
        title: 'Professional Team',
        experience: '5+ years in AI/ML, Blockchain & Enterprise Solutions development',
        projects: '10+ successful projects',
        certified: 'Certified Partners of leading cloud providers',
        members: [
          {
            role: 'AI/ML Engineer',
            skills: 'Python, TensorFlow, PyTorch, NLP, Computer Vision'
          },
          {
            role: 'Blockchain Developer',
            skills: 'Solidity, Web3.js, Smart Contracts, DeFi, NFT'
          },
          {
            role: 'Backend Developer',
            skills: 'Node.js, FastAPI, Docker, Kubernetes'
          },
          {
            role: 'Cloud Architect',
            skills: 'AWS, GCP, Azure, Infrastructure as Code'
          }
        ]
      }
    },
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive technology solutions for modern enterprises',
      packages: [
        {
          title: 'AI & Machine Learning Solutions',
          description: 'Advanced AI systems for communication and computer vision',
          features: [
            'Conversational AI & Chatbot Development',
            'Computer Vision & Image Recognition',
            'Natural Language Processing (NLP)',
            'Custom AI Model Training & Deployment',
            'Google Cloud Vision AI Integration',
            'Automated Document Processing (OCR)',
            'Real-time Object Detection & Tracking'
          ]
        },
        {
          title: 'Blockchain & Crypto Solutions',
          description: 'Blockchain and cryptocurrency solutions for enterprises and investors',
          features: [
            'Smart Contract Development (DeFi/NFT)',
            'Cryptocurrency Trading Bot Systems',
            'Blockchain Application (DApp) Development',
            'Wallet Integration & Payment Gateway',
            'Portfolio Management & Analytics Tools',
            'Market Data Analysis & Risk Management',
            'Crypto Exchange Integration'
          ]
        },
        {
          title: 'Enterprise Integration Services',
          description: 'System integration, modernization and digital transformation services',
          features: [
            'Digital Transformation & Process Automation',
            'Legacy System Modernization',
            'Cloud Migration & Infrastructure Setup',
            'API Development & Third-party Integration',
            'Database Design & Optimization',
            'DevOps & Continuous Deployment',
            'Security Implementation & Compliance',
            '24/7 System Monitoring & Support'
          ]
        }
      ]
    },
    clients: {
      title: 'Our Clients',
      subtitle: 'Leading organizations that trust our technology solutions',
      list: [
        {
          name: 'Sansiri',
          industry: 'Real Estate & Property',
          project: 'Sanroo - AI Assistant'
        }
      ]
    },
    contact: {
      title: 'Contact Us',
      address: '69/75 Phaya Thai Road, Thanon Phaya Thai, Ratchathewi, Bangkok 10400',
      phone: '+66 87-713-1110',
      email: 'contact@onionshack.com',
      form: {
        name: 'Name',
        email: 'Email',
        message: 'Message',
        send: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully! We will contact you within 24 hours',
        error: 'Error sending message. Please try again.'
      }
    },
    footer: {
      company: 'Onionshack Company Limited',
      tagline: 'Transforming Business Through AI, Blockchain & System Integration',
      description: 'Expert in developing AI Solutions, Blockchain & Crypto, and system integration services for leading organizations. With over 5 years of experience in creating modern and efficient technology solutions.',
      copyright: '© 2025 Onionshack Company Limited. All rights reserved.',
      services: {
        title: 'Services',
        list: ['AI & Machine Learning', 'Computer Vision', 'Blockchain & Crypto', 'Enterprise Integration']
      },
      contact: {
        title: 'Contact'
      },
      taxId: 'Tax ID: 0105560146233'
    }
  }
};